<template>
  <div class="w-full">
    <Panel header="Danh sách góp ý">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
        <section class="md:col-span-12">
          <div class="rounded-lg border border-slate-200 bg-white p-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div class="flex min-w-0 flex-col md:col-span-3">
                <label>Từ thời gian</label>
                <InputText type="datetime-local" v-model="timeFrom" />
              </div>
              <div class="flex min-w-0 flex-col md:col-span-3">
                <label>Đến thời gian</label>
                <InputText type="datetime-local" v-model="timeTo" />
              </div>
              <div class="flex min-w-0 flex-col md:col-span-3">
                <label>Phân loại</label>
                <Dropdown
                  v-model="selectedFeedbackKindId"
                  :options="feedbackKindOptions"
                  optionLabel="content"
                  optionValue="id"
                  placeholder="Tất cả"
                  :filter="true"
                  @change="loadFeedback()"
                />
              </div>
              <div class="flex min-w-0 flex-col justify-end md:col-span-2">
                <Button
                  label="Làm mới danh sách"
                  icon="pi pi-refresh"
                  iconPos="left"
                  :loading="isLoading"
                  :disabled="!timeFrom || !timeTo"
                  @click="loadFeedback()"
                />
              </div>
              <div class="flex flex-wrap items-end gap-2 md:col-span-12">
                <Button label="Hôm nay" outlined @click="selectToday" />
                <Button
                  label="7 ngày gần nhất"
                  outlined
                  @click="selectLast7Days"
                />
                <Button label="Tháng này" outlined @click="selectThisMonth" />
              </div>
            </div>
          </div>
        </section>

        <section class="md:col-span-12">
          <DataTable
            :loading="isLoading"
            :value="listFeedbackDetail"
            :paginator="true"
            :rows="10"
            v-model:filters="filters"
          >
            <template #header>
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div class="text-[15px] font-black text-slate-900">
                    Nội dung góp ý
                  </div>
                  <div class="text-sm font-medium text-slate-500">
                    Tổng số: {{ listFeedbackDetail.length }} góp ý
                  </div>
                </div>
                <span class="relative flex w-full max-w-[420px]">
                  <i
                    class="pi pi-search pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-slate-400"
                  />
                  <InputText
                    class="!pl-8 !pr-3"
                    v-model="filters['global'].value"
                    placeholder="Tìm theo tên, số điện thoại hoặc nội dung"
                  />
                </span>
              </div>
            </template>

            <Column field="fullName" header="Họ và tên" sortable></Column>
            <Column
              field="phoneNumber"
              header="Số điện thoại"
              sortable
            ></Column>
            <Column field="kind" header="Phân loại" sortable>
              <template #body="{ data }">
                {{ data.kind || "-" }}
              </template>
            </Column>
            <Column field="content" header="Nội dung">
              <template #body="{ data }">
                <div class="max-w-[560px] whitespace-pre-wrap leading-5">
                  {{ data.content || "-" }}
                </div>
              </template>
            </Column>
            <Column field="time" header="Thời gian gửi" sortable>
              <template #body="{ data }">
                {{ formatDateTime(data.time) }}
              </template>
            </Column>
          </DataTable>
        </section>
      </div>
    </Panel>
  </div>
</template>

<script lang="ts">
import { nextTick, ref } from "vue";
import { useToast } from "@/ui/services/toast";
import zaloRepository from "@/services/ZaloRepository";
import { FilterMatchMode } from "@/ui/services/api";
import { FeedbackItem, FeedbackKind } from "@/models/feedback";

function toDateTimeLocalValue(date: Date) {
  const offsetDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  );
  return offsetDate.toISOString().slice(0, 16);
}

function toIsoString(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

export default {
  setup() {
    const toast = useToast();
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);

    const timeFrom = ref(toDateTimeLocalValue(startOfDay));
    const timeTo = ref(toDateTimeLocalValue(now));
    const listFeedbackDetail = ref([] as FeedbackItem[]);
    const selectedFeedbackKindId = ref(0);
    const feedbackKindOptions = ref<FeedbackKind[]>([
      { id: 0, content: "Tất cả" },
    ]);
    const isLoading = ref(false);

    const filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const clearFilter = () => {
      filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
    };

    const formatDateTime = (value: string) => {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return new Intl.DateTimeFormat("vi-VN", {
        dateStyle: "short",
        timeStyle: "medium",
      }).format(date);
    };

    const loadFeedbackKind = () => {
      zaloRepository
        .getListFeedbackKind()
        .then((response) => {
          feedbackKindOptions.value = [
            { id: 0, content: "Tất cả" },
            ...(response.data || []),
          ];
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Lỗi",
            detail: err.response?.data || "Lỗi khi tải loại góp ý",
            life: 2500,
          });
        });
    };

    const loadFeedback = async () => {
      const timeFromIso = toIsoString(timeFrom.value);
      const timeToIso = toIsoString(timeTo.value);

      if (!timeFromIso || !timeToIso) {
        toast.add({
          severity: "error",
          summary: "Lỗi",
          detail: "Khoảng thời gian không hợp lệ",
          life: 2500,
        });
        return;
      }

      isLoading.value = true;
      await nextTick();
      const loadingStartedAt = Date.now();

      zaloRepository
        .getListFeedBack({
          timeFrom: timeFromIso,
          timeTo: timeToIso,
          idKind: Number(selectedFeedbackKindId.value || 0),
        })
        .then((response) => {
          listFeedbackDetail.value = response.data || [];
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Lỗi",
            detail: err.response?.data || "Lỗi khi tải danh sách góp ý",
            life: 2500,
          });
        })
        .finally(() => {
          const elapsed = Date.now() - loadingStartedAt;
          window.setTimeout(() => {
            isLoading.value = false;
          }, Math.max(0, 300 - elapsed));
        });
    };

    const selectToday = () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      timeFrom.value = toDateTimeLocalValue(start);
      timeTo.value = toDateTimeLocalValue(end);
      loadFeedback();
    };

    const selectLast7Days = () => {
      const start = new Date();
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      timeFrom.value = toDateTimeLocalValue(start);
      timeTo.value = toDateTimeLocalValue(end);
      loadFeedback();
    };

    const selectThisMonth = () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      timeFrom.value = toDateTimeLocalValue(start);
      timeTo.value = toDateTimeLocalValue(end);
      loadFeedback();
    };

    loadFeedbackKind();
    loadFeedback();

    return {
      filters,
      clearFilter,
      isLoading,
      timeFrom,
      timeTo,
      selectedFeedbackKindId,
      feedbackKindOptions,
      selectToday,
      selectLast7Days,
      selectThisMonth,
      listFeedbackDetail,
      loadFeedback,
      formatDateTime,
    };
  },
};
</script>
